import React, { useState, useEffect, useRef } from 'react';
import {
  Flex,
  Typography,
  Button,
  Card,
  Table,
  Tag,
  Avatar,
  Modal,
  Form,
  Input,
  Select,
  Spin,
  Space,
  Row,
  Col,
  Pagination,
  Collapse,
  Tooltip,
  Switch,
  Alert,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
  BankOutlined,
  UserOutlined,
  ReloadOutlined,
  PhoneOutlined,
  MailOutlined,
  ShopOutlined,
  SearchOutlined,
  ClearOutlined,
  FilterOutlined,
  DownOutlined,
  UpOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../i18n/TranslationContext';
import { useNotification } from '../../hooks/useNotification';
import DashboardService from '../../service';
import CreatorInfo from '../../components/CreatorInfo';
import { useIsMobile, useIsTablet } from '../../hooks/useResponsive';

const DealersPage = () => {
  const { user, canCreateDealer, canManageUser } = useAuth();
  const navigate = useNavigate();
  const { lang } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const [dealers, setDealers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchStats, setBranchStats] = useState(null);
  const [copartAccounts, setCopartAccounts] = useState([]);
  const [iaaiAccounts, setIaaiAccounts] = useState([]);
  const [iaaiCanadaAccounts, setIaaiCanadaAccounts] = useState([]);
  const [manheimAccounts, setManheimAccounts] = useState([]);
  const [acvAccounts, setAcvAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(40);
  const [totalCount, setTotalCount] = useState(0);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingDealer, setEditingDealer] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [searchFilters, setSearchFilters] = useState({});
  const [searchForm, setSearchForm] = useState({
    quickSearch: '',
    name: '',
    user_name: '',
    email: '',
    phone: '',
    branchId: '',
    managerId: ''
  });
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    branchId: '',
    managerId: '',
    copartId: '',
    copartIdSecondary: '',
    iaaiId: '',
    iaaiIdSecondary: '',
    iaaiCanadaId: '',
    iaaiCanadaIdSecondary: '',
    manheimId: '',
    manheimIdSecondary: '',
    acvId: '',
    acvIdSecondary: '',
    status: 'active',
    // Auction settings
    copartOn: true,
    iaaiOn: true,
    iaaiCanadaOn: false,
    manheimOn: false,
    acvOn: false,
    prebid: true,
    liveBid: true,
    unlimited: false,
    // Limits
    bidLimit: '0',
    activeBidsLimit: '0',
    activeBidsSumLimit: '0',
    purchasedCarsLimit: '0',
    // Settings
    lotsEventSetting: 'hide',
    purchasedLotsSetting: 'winning_lots',
    packageSlug: 'run_drive',
    // Calculator Settings
    show_calculator: true,
    show_helper: false,
    show_discount: false,
    discountAmount: 0
  });

  const quickSearchInputRef = useRef(null);

  // Focus search input on Ctrl+F / Cmd+F
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault();
        quickSearchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Initialize from URL parameters and load data on mount
  useEffect(() => {
    const urlPage = parseInt(searchParams.get('page')) || 1;
    const urlQuickSearch = searchParams.get('quickSearch') || '';
    const urlName = searchParams.get('name') || '';
    const urlUserName = searchParams.get('user_name') || '';
    const urlEmail = searchParams.get('email') || '';
    const urlPhone = searchParams.get('phone') || '';
    const urlBranchId = searchParams.get('branchId') || '';
    const urlManagerId = searchParams.get('managerId') || '';

    // Build filters from URL
    const urlFilters = {};
    if (urlQuickSearch) urlFilters.quickSearch = urlQuickSearch;
    if (urlName) urlFilters.name = urlName;
    if (urlUserName) urlFilters.user_name = urlUserName;
    if (urlEmail) urlFilters.email = urlEmail;
    if (urlPhone) urlFilters.phone = urlPhone;
    if (urlBranchId) urlFilters.branchId = urlBranchId;
    if (urlManagerId) urlFilters.managerId = urlManagerId;

    // Set form state from URL
    setSearchForm({
      quickSearch: urlQuickSearch,
      name: urlName,
      user_name: urlUserName,
      email: urlEmail,
      phone: urlPhone,
      branchId: urlBranchId,
      managerId: urlManagerId
    });

    // Set page from URL
    setPage(urlPage);

    // Set filters and search state
    if (Object.keys(urlFilters).length > 0) {
      setSearchFilters(urlFilters);
      setIsSearching(true);
    } else {
      setSearchFilters({});
      setIsSearching(false);
    }

    // Load initial data
    loadBranches();
    loadBranchStats();
    
    // Load dealers with URL filters/page
    if (Object.keys(urlFilters).length > 0) {
      loadDealers(urlPage, false, urlFilters);
    } else {
      loadDealers(urlPage, false);
    }
  }, [user]); // Run when user changes (initial mount or user update)

  // Auto-select first available account when accounts are loaded and we are creating a new dealer
  useEffect(() => {
    if (isFormOpen && !editingDealer) {
      setFormData(prev => {
        const newData = { ...prev };
        let changed = false;

        const getFirstAccount = (accounts) => {
          const valid = accounts.filter(acc => acc.type === 'primary' || acc.type === 'master' || !acc.type);
          return valid.length > 0 ? (valid[0]._id || valid[0].id) : '';
        };

        if (!newData.copartId && copartAccounts.length > 0) {
          const first = getFirstAccount(copartAccounts);
          if (first) { newData.copartId = first; changed = true; }
        }
        if (!newData.iaaiId && iaaiAccounts.length > 0) {
          const first = getFirstAccount(iaaiAccounts);
          if (first) { newData.iaaiId = first; changed = true; }
        }
        if (!newData.iaaiCanadaId && iaaiCanadaAccounts.length > 0) {
          const first = getFirstAccount(iaaiCanadaAccounts);
          if (first) { newData.iaaiCanadaId = first; changed = true; }
        }
        if (!newData.manheimId && manheimAccounts.length > 0) {
          const first = getFirstAccount(manheimAccounts);
          if (first) { newData.manheimId = first; changed = true; }
        }
        if (!newData.acvId && acvAccounts.length > 0) {
          const first = getFirstAccount(acvAccounts);
          if (first) { newData.acvId = first; changed = true; }
        }

        return changed ? newData : prev;
      });
    }
  }, [isFormOpen, editingDealer, copartAccounts, iaaiAccounts, iaaiCanadaAccounts, manheimAccounts, acvAccounts]);

  // Load managers when branch is selected (for both create and edit flows)
  useEffect(() => {
    if (isFormOpen && formData.branchId) {
      // Load managers when a branch is selected
      loadManagers();
    }
  }, [isFormOpen, formData.branchId]);

  // Auto-select branch and manager if only one value when creating new dealer
  useEffect(() => {
    if (isFormOpen && !editingDealer) {
      // Auto-select branch if only one (for super_admin)
      if (user.role === 'super_admin' && branches.length === 1 && !formData.branchId) {
        setFormData(prev => ({
          ...prev,
          branchId: branches[0].id.toString()
        }));
      }

      // Auto-select manager if only one (for super_admin, check all managers)
      if (user.role === 'super_admin' && managers.length === 1 && !formData.managerId) {
        setFormData(prev => ({
          ...prev,
          managerId: managers[0].user_id || managers[0].id
        }));
      }

      // For manager role, auto-select their own manager ID
      if (user.role === 'manager' && !formData.managerId && user.user_id) {
        setFormData(prev => ({
          ...prev,
          managerId: user.user_id
        }));
      }
    }
  }, [isFormOpen, editingDealer, branches, managers, formData.branchId, formData.managerId, user.role, user.user_id]);

  const extractDealersPayload = (data) => {
    // Prioritize totalUsers from backend for statistics
    const list = data?.users || data?.data?.users || (Array.isArray(data) ? data : []);
    const meta = {
      total: (typeof data?.totalUsers === 'number' ? data.totalUsers : undefined)
        ?? data?.pagination?.totalUsers
        ?? data?.total
        ?? data?.count
        ?? 0,
      hasNext: data?.hasNextPage ?? data?.pagination?.hasNextPage ?? (Array.isArray(list) && list.length === limit),
    };
    return { list, meta };
  };

  const loadDealers = async (nextPage = 1, append = false, forceFilters = null) => {
    try {
      setLoading(true);
      let data;

      const currentFilters = forceFilters !== null ? forceFilters : searchFilters;
      const shouldSearch = forceFilters !== null ? Object.keys(forceFilters).length > 0 : (isSearching && Object.keys(searchFilters).length > 0);

      if (shouldSearch) {
        // Use search endpoint
        data = await DashboardService.searchUsers({
          ...currentFilters,
          role: 'dealer',
          page: nextPage,
          limit,
          sortBy: 'createdAt',
          sortOrder: 'desc'
        });
      } else {
        // Use regular endpoint
        data = await DashboardService.getDealers(nextPage, limit, 'createdAt', 'desc');
      }

      const { list, meta } = extractDealersPayload(data);
      if (Array.isArray(list)) {
        setDealers(list);
        setPage(nextPage);
        const knownTotal = meta.total || 0;
        setTotalCount(knownTotal || (append ? totalCount : list.length));
      }
    } catch (error) {
      console.error('Failed to load dealers:', error);
      showError('Failed to load dealers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to update URL parameters
  const updateURLParams = (newFilters, newPage) => {
    const params = new URLSearchParams();
    
    // Include page if greater than 1
    if (newPage && newPage > 1) {
      params.set('page', newPage.toString());
    }
    
    // Include all filters
    Object.entries(newFilters || {}).forEach(([key, value]) => {
      if (value && value.toString().trim() !== '') {
        params.set(key, value.toString().trim());
      }
    });
    
    // Update URL without causing navigation
    setSearchParams(params, { replace: true });
  };

  const handleRefresh = () => {
    setDealers([]);
    setPage(1);
    setTotalCount(0);
    setSearchFilters({});
    setSearchForm({
      quickSearch: '',
      name: '',
      user_name: '',
      email: '',
      phone: '',
      branchId: '',
      managerId: ''
    });
    setIsSearching(false);
    setShowAdvancedFilters(false);
    // Clear URL parameters
    setSearchParams({}, { replace: true });
    loadDealers(1, false);
  };

  const handleQuickSearch = () => {
    const value = searchForm.quickSearch.trim();

    if (value) {
      const filters = { quickSearch: value };
      // Clear advanced filters when using quick search
      setSearchForm(prev => ({
        quickSearch: prev.quickSearch,
        name: '',
        user_name: '',
        email: '',
        phone: '',
        branchId: '',
        managerId: ''
      }));
      setSearchFilters(filters);
      setIsSearching(true);
      setPage(1);
      updateURLParams(filters, 1);
      loadDealers(1, false, filters); // Pass filters directly
    } else {
      showError('Please enter a search term');
    }
  };

  const handleQuickSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleQuickSearch();
    }
  };

  const handleFormChange = (field, value) => {
    setSearchForm({ ...searchForm, [field]: value });
  };

  const handleAdvancedSearch = () => {
    const filters = {};
    Object.entries(searchForm).forEach(([key, value]) => {
      if (value && value.toString().trim() !== '' && key !== 'quickSearch') {
        filters[key] = value;
      }
    });

    if (Object.keys(filters).length > 0) {
      // Clear quickSearch when using advanced filters
      setSearchForm(prev => ({ ...prev, quickSearch: '' }));
      setSearchFilters(filters);
      setIsSearching(true);
      setPage(1);
      updateURLParams(filters, 1);
      loadDealers(1, false, filters); // Pass filters directly
    } else {
      showError('Please enter at least one search criterion');
    }
  };

  const clearSearch = () => {
    setSearchFilters({});
    setSearchForm({
      quickSearch: '',
      name: '',
      user_name: '',
      email: '',
      phone: '',
      branchId: '',
      managerId: ''
    });
    setIsSearching(false);
    setShowAdvancedFilters(false);
    setPage(1);
    // Clear URL parameters
    setSearchParams({}, { replace: true });
    loadDealers(1, false);
  };

  const getActiveFilterCount = () => {
    return Object.values(searchFilters).filter(value => value && value.toString().trim() !== '').length;
  };

  const loadManagers = async () => {
    try {
      // Load managers - super admin sees all, manager sees self
      const data = await DashboardService.getManagers(1, 100, 'createdAt', 'desc');
      console.log('Managers API response:', data);
      if (data.success && data.users) {
        console.log('Setting managers:', data.users);
        setManagers(data.users);
      } else if (data.users) {
        // Fallback for non-wrapped response
        console.log('Setting managers (fallback):', data.users);
        setManagers(data.users);
      }
    } catch (error) {
      console.error('Failed to load managers:', error);
      showError('Failed to load managers list');
    }
  };

  const loadBranches = async () => {
    try {
      const data = await DashboardService.getBranches();
      if (data.success && data.branches) {
        setBranches(data.branches);
      } else if (data.branches) {
        // Fallback for non-wrapped response
        setBranches(data.branches);
      }
    } catch (error) {
      console.error('Failed to load branches:', error);
      showError('Failed to load branches list');
    }
  };

  const loadBranchStats = async () => {
    try {
      const data = await DashboardService.getDealersByBranch();
      if (data.success) {
        setBranchStats(data);
      }
    } catch (error) {
      console.error('Failed to load branch statistics:', error);
      // Don't show error - stats are optional
    }
  };

  const loadAuctionAccounts = async () => {
    try {
      const data = await DashboardService.getAuctionAccounts({ page: 1, limit: 100, sortBy: 'created_at', sortOrder: 'desc' });
      if (data.success && data.accounts) {
        setCopartAccounts(data.accounts.filter(acc => acc.auction?.toLowerCase() === 'copart'));
        setIaaiAccounts(data.accounts.filter(acc => acc.auction?.toLowerCase() === 'iaai'));
        setIaaiCanadaAccounts(data.accounts.filter(acc => acc.auction?.toLowerCase() === 'iaai_canada' || acc.auction?.toLowerCase() === 'iaai canada'));
        setManheimAccounts(data.accounts.filter(acc => acc.auction?.toLowerCase() === 'manheim'));
        setAcvAccounts(data.accounts.filter(acc => acc.auction?.toLowerCase() === 'acv'));
      }
    } catch (error) {
      console.error('Failed to load auction accounts:', error);
      // Don't show error - auction accounts are optional
    }
  };

  // Get available managers based on user role
  const getAvailableManagers = () => {
    console.log('getAvailableManagers - user.role:', user.role);
    console.log('getAvailableManagers - managers:', managers);
    console.log('getAvailableManagers - user.user_id:', user.user_id);
    if (user.role === 'super_admin') {
      return managers;
    } else if (user.role === 'manager') {
      // Filter to show only current manager - check user_id match
      const filtered = managers.filter(m => {
        const managerUserId = m.user_id || m.id || m._id;
        const currentUserId = user.user_id || user.id || user._id;
        const matches = managerUserId === currentUserId;
        console.log(`Manager ${m.name} user_id ${managerUserId} matches current user ${currentUserId}:`, matches);
        return matches;
      });
      console.log('Filtered managers for manager role:', filtered);
      return filtered;
    }
    return [];
  };

  const handleCreate = () => {
    setEditingDealer(null);
    setFormData({
      username: '',
      password: '',
      confirmPassword: '',
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      branchId: user.role === 'manager' ? user.branchId : '',
      managerId: user.role === 'manager' ? (user.user_id || user.managerId || user.autoIncrementId || user.id) : '',
      copartId: '',
      copartIdSecondary: '',
      iaaiId: '',
      iaaiIdSecondary: '',
      iaaiCanadaId: '',
      iaaiCanadaIdSecondary: '',
      manheimId: '',
      manheimIdSecondary: '',
      acvId: '',
      acvIdSecondary: '',
      status: 'active',
      // Reset auction settings
      copartOn: false,
      iaaiOn: false,
      iaaiCanadaOn: false,
      manheimOn: false,
      acvOn: false,
      prebid: true,
      liveBid: true,
      unlimited: false,
      bidLimit: '0',
      activeBidsLimit: '0',
      activeBidsSumLimit: '0',
      purchasedCarsLimit: '0',
      lotsEventSetting: 'hide',
      purchasedLotsSetting: 'winning_lots',
      packageSlug: 'run_drive',
      // Calculator Settings
      show_calculator: true,
      show_helper: false,
      show_discount: false,
      discountAmount: 0
    });

    // Load branches and auction accounts when form opens
    // Managers will be loaded when branch is selected (via useEffect)
    loadBranches();
    loadAuctionAccounts();

    setIsFormOpen(true);
  };

  const handleEdit = (dealer) => {
    setEditingDealer(dealer);

    // Load managers, branches, and auction accounts for editing
    loadManagers();
    loadBranches();
    loadAuctionAccounts();

    setFormData({
      username: dealer.user_name || '',
      password: '',
      confirmPassword: '',
      email: dealer.email || '',
      firstName: dealer.firstName || '',
      lastName: dealer.lastName || '',
      phone: dealer.phone || '',
      branchId: dealer.branchId || '',
      managerId: dealer.managerId || '', // Backend should already have user_id format
      copartId: dealer.copart_id || '',
      copartIdSecondary: dealer.copart_id_secondary || '',
      iaaiId: dealer.iaai_id || '',
      iaaiIdSecondary: dealer.iaai_id_secondary || '',
      iaaiCanadaId: dealer.iaai_canada_id || '',
      iaaiCanadaIdSecondary: dealer.iaai_canada_id_secondary || '',
      manheimId: dealer.manheim_id || '',
      manheimIdSecondary: dealer.manheim_id_secondary || '',
      acvId: dealer.acv_id || '',
      acvIdSecondary: dealer.acv_id_secondary || '',
      status: dealer.status || 'active',
      // Auction settings with defaults
      copartOn: dealer.copart_on !== undefined ? dealer.copart_on : true,
      iaaiOn: dealer.iaai_on !== undefined ? dealer.iaai_on : true,
      iaaiCanadaOn: dealer.iaai_canada_on !== undefined ? dealer.iaai_canada_on : true,
      manheimOn: dealer.manheim_on !== undefined ? dealer.manheim_on : false,
      acvOn: dealer.acv_on !== undefined ? dealer.acv_on : true,
      prebid: dealer.prebid !== undefined ? dealer.prebid : true,
      liveBid: dealer.live_bid !== undefined ? dealer.live_bid : true,
      unlimited: dealer.unlimited !== undefined ? dealer.unlimited : false,
      bidLimit: dealer.bet_limit?.limit?.toString() || '0',
      activeBidsLimit: dealer.bet_limit?.active_bids_limit?.toString() || '0',
      activeBidsSumLimit: dealer.bet_limit?.active_bids_sum_limit?.toString() || '0',
      purchasedCarsLimit: dealer.pay_limit?.count_limit?.toString() || '0',
      lotsEventSetting: dealer.lots_event_setting || 'hide',
      purchasedLotsSetting: dealer.purchased_lots_setting || 'winning_lots',
      packageSlug: dealer.package_slug || 'run_drive',
      // Calculator Settings
      show_calculator: dealer.show_calculator !== undefined ? dealer.show_calculator : true,
      show_helper: dealer.show_helper !== undefined ? dealer.show_helper : false,
      show_discount: dealer.show_discount !== undefined ? dealer.show_discount : false,
      discountAmount: dealer.discountAmount || 0
    });
    setIsFormOpen(true);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete dealer "${name}"?`)) {
      return;
    }

    try {
      console.log('Deleting dealer with ID:', id);
      const result = await DashboardService.deleteUser(id);
      console.log('Delete result:', result);

      if (result.success) {
        showSuccess(result.message || 'Dealer deleted successfully!');
        loadDealers(1); // Reload from page 1
      } else {
        showError(result.error || result.message || 'Failed to delete dealer');
      }
    } catch (error) {
      console.error('Failed to delete dealer:', error);
      showError(error.message || 'Failed to delete dealer. Please try again.');
    }
  };

  // Check if at least one auction account is selected
  const hasAuctionAccount = () => {
    return (formData.copartId && formData.copartId !== '' && formData.copartOn) ||
      (formData.iaaiId && formData.iaaiId !== '' && formData.iaaiOn) ||
      (formData.iaaiCanadaId && formData.iaaiCanadaId !== '' && formData.iaaiCanadaOn) ||
      (formData.manheimId && formData.manheimId !== '' && formData.manheimOn) ||
      (formData.acvId && formData.acvId !== '' && formData.acvOn);
  };

  const handleFormSubmit = async () => {
    // Validate auction account for new dealers
    if (!editingDealer && !hasAuctionAccount()) {
      showError('Please select at least one auction account (Copart or IAAI)');
      return;
    }

    // Validate password match (only for new dealers)
    if (!editingDealer) {
      if (formData.password !== formData.confirmPassword) {
        showError('Passwords do not match!');
        return;
      }

      if (formData.password.length < 6) {
        showError('Password must be at least 6 characters');
        return;
      }
    } else {
      // When editing, validate if password is provided
      if (formData.password && formData.password.length > 0) {
        if (formData.password !== formData.confirmPassword) {
          showError('Passwords do not match!');
          return;
        }

        if (formData.password.length < 6) {
          showError('Password must be at least 6 characters');
          return;
        }
      }
    }

    try {
      const branch = branches.find(b => b.id === parseInt(formData.branchId));
      // Find manager by user_id (USER format)
      const manager = managers.find(m => m.user_id === formData.managerId);

      // Validate manager exists before submitting
      if (formData.managerId && !manager) {
        showError('Selected manager not found. Please select a valid manager.');
        return;
      }

      // Validate branch exists before submitting
      if (formData.branchId && !branch) {
        showError('Selected branch not found. Please select a valid branch.');
        return;
      }

      if (editingDealer) {
        // Update existing dealer
        const updateData = {
          name: `${formData.firstName} ${formData.lastName}`, // Update full name when firstName/lastName changes
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          branchId: formData.branchId && formData.branchId !== '' ? parseInt(formData.branchId) : editingDealer.branchId,
          managerId: formData.managerId && formData.managerId !== '' ? formData.managerId : editingDealer.managerId, // Send user_id (USER format)
          dealerId: editingDealer.dealerId || editingDealer.id, // Preserve dealerId
          copart_id: formData.copartId && formData.copartId !== '' ? formData.copartId : null,
          copart_id_secondary: formData.copartIdSecondary && formData.copartIdSecondary !== '' ? formData.copartIdSecondary : null,
          iaai_id: formData.iaaiId && formData.iaaiId !== '' ? formData.iaaiId : null,
          iaai_id_secondary: formData.iaaiIdSecondary && formData.iaaiIdSecondary !== '' ? formData.iaaiIdSecondary : null,
          iaai_canada_id: formData.iaaiCanadaId && formData.iaaiCanadaId !== '' ? formData.iaaiCanadaId : null,
          iaai_canada_id_secondary: formData.iaaiCanadaIdSecondary && formData.iaaiCanadaIdSecondary !== '' ? formData.iaaiCanadaIdSecondary : null,
          manheim_id: formData.manheimId && formData.manheimId !== '' ? formData.manheimId : null,
          manheim_id_secondary: formData.manheimIdSecondary && formData.manheimIdSecondary !== '' ? formData.manheimIdSecondary : null,
          acv_id: formData.acvId && formData.acvId !== '' ? formData.acvId : null,
          acv_id_secondary: formData.acvIdSecondary && formData.acvIdSecondary !== '' ? formData.acvIdSecondary : null,
          // Auction settings
          copart_on: formData.copartOn,
          iaai_on: formData.iaaiOn,
          iaai_canada_on: formData.iaaiCanadaOn,
          manheim_on: formData.manheimOn,
          acv_on: formData.acvOn,
          prebid: formData.prebid,
          live_bid: formData.liveBid,
          unlimited: formData.unlimited,
          // Limits
          bet_limit: {
            summ_one_bet: parseInt(formData.bidLimit) || 0,
            limit: parseInt(formData.bidLimit) || 0,
            count: parseInt(formData.activeBidsLimit) || 0,
            active_bids_limit: parseInt(formData.activeBidsLimit) || 0,
            active_bids_sum_limit: parseInt(formData.activeBidsSumLimit) || 0,
            active_bids_sum: editingDealer.bet_limit?.active_bids_sum || 0
          },
          pay_limit: {
            count_limit: parseInt(formData.purchasedCarsLimit) || 0,
            count_pay: editingDealer.pay_limit?.count_pay || 0
          },
          // Settings
          lots_event_setting: formData.lotsEventSetting,
          purchased_lots_setting: formData.purchasedLotsSetting,
          package_slug: formData.packageSlug,
          // Calculator Settings
          show_calculator: formData.show_calculator,
          show_helper: formData.show_helper,
          show_discount: formData.show_discount,
          discountAmount: formData.discountAmount
        };

        // Include password only if provided
        if (formData.password && formData.password.trim() !== '') {
          updateData.password = formData.password;
        }

        console.log('Updating dealer with data:', updateData); // Debug log

        // Use user_id (primary), dealerId, autoIncrementId, or id
        const dealerId = editingDealer.user_id || editingDealer.dealerId || editingDealer.autoIncrementId || editingDealer.id;
        console.log('Updating dealer with ID:', dealerId, 'from dealer:', editingDealer); // Debug log

        if (!dealerId) {
          showError('Cannot update dealer: ID not found');
          return;
        }

        const result = await DashboardService.updateUser(dealerId, updateData);

        if (result.success) {
          showSuccess('Dealer updated successfully!');
          loadDealers();
        } else {
          // Handle specific error messages
          let errorMessage = result.error || result.message || 'Failed to update dealer';

          // Provide more user-friendly error messages
          if (errorMessage.toLowerCase().includes('manager not found')) {
            errorMessage = 'Manager not found. Please select a valid manager.';
          } else if (errorMessage.toLowerCase().includes('branch not found')) {
            errorMessage = 'Branch not found. Please select a valid branch.';
          } else if (errorMessage.toLowerCase().includes('dealer not found')) {
            errorMessage = 'Dealer not found. The dealer may have been deleted.';
          } else if (errorMessage.toLowerCase().includes('username') && errorMessage.toLowerCase().includes('already')) {
            errorMessage = 'Username already exists. Please choose a different username.';
          } else if (errorMessage.toLowerCase().includes('email') && errorMessage.toLowerCase().includes('already')) {
            errorMessage = 'Email already exists. Please use a different email address.';
          }

          console.error('Failed to update dealer:', result);
          showError(errorMessage);
          return; // Don't close form if failed
        }
      } else {
        // Create new dealer
        const dealerData = {
          user_id: `USER${Date.now()}`,
          first_uuid: `UUID1-${Date.now()}`,
          second_uuid: `UUID2-${Date.now()}`,
          user_name: formData.username,
          password: formData.password,
          email: formData.email,
          role: 'dealer',
          name: `${formData.firstName} ${formData.lastName}`, // Required field
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          branchId: formData.branchId && formData.branchId !== '' ? parseInt(formData.branchId) : null,
          branchName: branch?.name,
          managerId: formData.managerId && formData.managerId !== '' ? formData.managerId : null, // Send user_id (USER format)
          managerName: manager?.name,
          copart_id: formData.copartId || null,
          copart_id_secondary: formData.copartIdSecondary || null,
          iaai_id: formData.iaaiId || null,
          iaai_id_secondary: formData.iaaiIdSecondary || null,
          iaai_canada_id: formData.iaaiCanadaId || null,
          iaai_canada_id_secondary: formData.iaaiCanadaIdSecondary || null,
          manheim_id: formData.manheimId || null,
          manheim_id_secondary: formData.manheimIdSecondary || null,
          acv_id: formData.acvId || null,
          acv_id_secondary: formData.acvIdSecondary || null,
          // Auction settings
          copart_on: formData.copartOn,
          iaai_on: formData.iaaiOn,
          iaai_canada_on: formData.iaaiCanadaOn,
          manheim_on: formData.manheimOn,
          acv_on: formData.acvOn,
          prebid: formData.prebid,
          live_bid: formData.liveBid,
          unlimited: formData.unlimited,
          // Limits
          bet_limit: {
            summ_one_bet: parseInt(formData.bidLimit) || 0,
            limit: parseInt(formData.bidLimit) || 0,
            count: parseInt(formData.activeBidsLimit) || 0,
            active_bids_limit: parseInt(formData.activeBidsLimit) || 0,
            active_bids_sum_limit: parseInt(formData.activeBidsSumLimit) || 0,
            active_bids_sum: 0
          },
          pay_limit: {
            count_limit: parseInt(formData.purchasedCarsLimit) || 0,
            count_pay: 0
          },
          // Settings
          lots_event_setting: formData.lotsEventSetting,
          purchased_lots_setting: formData.purchasedLotsSetting,
          package_slug: formData.packageSlug,
          // Calculator Settings
          show_calculator: formData.show_calculator,
          show_helper: formData.show_helper,
          show_discount: formData.show_discount,
          discountAmount: formData.discountAmount,
          // Note: dealerId is auto-generated by backend after creation
          permissions: ['view_own_data', 'place_bids', 'manage_subdealers', 'view_subdealer_bids']
        };

        const result = await DashboardService.createUser(dealerData);

        if (result.success) {
          showSuccess('Dealer created successfully!');
          loadDealers();
        } else {
          // Handle specific error messages
          let errorMessage = result.error || result.message || 'Failed to create dealer';

          // Provide more user-friendly error messages
          if (errorMessage.toLowerCase().includes('manager not found')) {
            errorMessage = 'Manager not found. Please select a valid manager.';
          } else if (errorMessage.toLowerCase().includes('branch not found')) {
            errorMessage = 'Branch not found. Please select a valid branch.';
          } else if (errorMessage.toLowerCase().includes('dealer not found')) {
            errorMessage = 'Dealer not found. Please select a valid dealer.';
          } else if (errorMessage.toLowerCase().includes('username') && errorMessage.toLowerCase().includes('already')) {
            errorMessage = 'Username already exists. Please choose a different username.';
          } else if (errorMessage.toLowerCase().includes('email') && errorMessage.toLowerCase().includes('already')) {
            errorMessage = 'Email already exists. Please use a different email address.';
          }

          console.error('Failed to create dealer:', result);
          showError(errorMessage);
          return; // Don't close form if failed
        }
      }

      setIsFormOpen(false);
      setEditingDealer(null);
    } catch (error) {
      console.error('Failed to save dealer:', error);

      // Handle network errors or unexpected errors
      let errorMessage = 'Failed to save dealer. Please try again.';

      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      // Provide more user-friendly error messages
      if (errorMessage.toLowerCase().includes('manager not found')) {
        errorMessage = 'Manager not found. Please select a valid manager.';
      } else if (errorMessage.toLowerCase().includes('branch not found')) {
        errorMessage = 'Branch not found. Please select a valid branch.';
      } else if (errorMessage.toLowerCase().includes('network') || errorMessage.toLowerCase().includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      showError(errorMessage);
    }
  };

  const handleDealerClick = (dealer) => {
    // Use user_id (from API), dealerId, autoIncrementId, or id - whichever exists
    const dealerId = dealer.user_id || dealer.dealerId || dealer.autoIncrementId || dealer.id;
    console.log('Navigating to dealer with ID:', dealerId, 'from dealer object:', dealer);
    if (dealerId) {
      navigate(`/${lang}/dealers/${dealerId}`);
    } else {
      console.error('No valid ID found for dealer:', dealer);
    }
  };

  const getBranchName = (branchId) => {
    if (!branchId) return t('dealers.unknownBranch');
    const branch = branches.find(b => b.id === branchId || b.id === parseInt(branchId));
    return branch?.name || t('dealers.unknownBranch');
  };

  const getBranchFlag = (branchId) => {
    if (!branchId) return '🏢';
    const branch = branches.find(b => b.id === branchId || b.id === parseInt(branchId));
    return branch?.flag || '🏢';
  };

  const AuctionAccountCard = ({ title, shortLabel, color, on, setOn, primaryValue, primaryOptions, onPrimaryChange, secondaryValue, secondaryOptions, onSecondaryChange }) => (
    <Card size="small" style={{ height: '100%', transition: 'all 0.3s ease', borderColor: on ? color : undefined }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Flex align="center" gap={12}>
          <Avatar style={{ backgroundColor: on ? color : '#bdbdbd', width: 32, height: 32, fontSize: '0.9rem' }}>{shortLabel}</Avatar>
          <Typography.Text strong style={{ fontSize: '1rem', color: on ? color : '#757575' }}>{title}</Typography.Text>
        </Flex>
        <Space>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{on ? t('dealers.toggleOn') : t('dealers.toggleOff')}</Typography.Text>
          <Switch checked={on} onChange={setOn} size="small" />
        </Space>
      </Flex>
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <Select
          placeholder={t('dealers.accountAmPlaceholder')}
          value={primaryValue || undefined}
          onChange={onPrimaryChange}
          disabled={!on}
          size="small"
          style={{ width: '100%' }}
          options={primaryOptions.map(acc => ({ value: acc._id || acc.id, label: `${acc.account} - Buyer #${acc.buyer_number}` }))}
        />
        <Select
          placeholder={t('dealers.accountUsOptional')}
          value={secondaryValue === '' ? '' : (secondaryValue || undefined)}
          onChange={(v) => onSecondaryChange(v === undefined ? '' : (v || ''))}
          disabled={!on}
          size="small"
          style={{ width: '100%' }}
          allowClear
          options={[{ value: '', label: t('dealers.noneOption') }, ...secondaryOptions.map(acc => ({ value: acc._id || acc.id, label: `${acc.account} - Buyer #${acc.buyer_number}` }))]}
        />
      </Space>
    </Card>
  );

  // Statistics - use API totals when available
  const stats = [
    {
      title: t('dealers.totalDealers'),
      value: branchStats?.totals?.totalDealers || totalCount || dealers.length,
      icon: <ShopOutlined />,
      color: '#1e3a5f'
    },
    {
      title: t('managers.totalManagers'),
      value: branchStats?.totals?.totalManagers || 0,
      icon: <UserOutlined />,
      color: '#22c55e'
    },
    {
      title: t('dealers.totalSubdealers'),
      value: branchStats?.totals?.totalSubdealers || dealers.reduce((sum, d) => sum + (d.stats?.totalSubdealers || 0), 0),
      icon: <TeamOutlined />,
      color: '#f59e0b'
    },
    {
      title: t('branches.totalBranches'),
      value: branchStats?.totals?.totalBranches || branches.length,
      icon: <BankOutlined />,
      color: '#8b5cf6'
    },
    ...(branchStats?.branches || []).map((branch, index) => ({
      title: `${branch.flag} ${branch.branchName}`,
      value: branch.dealerCount,
      icon: <ShopOutlined />,
      color: ['#22c55e', '#f59e0b', '#8b5cf6', '#6366f1', '#0ea5e9'][index % 5]
    }))
  ];

  const columns = [
    {
      title: t('dealers.colDealer'),
      key: 'dealer',
      render: (_, record) => (
        <Flex align="center" gap={8}>
          <Avatar
            size={40}
            shape="circle"
            style={{ backgroundColor: '#1e3a5f', flexShrink: 0 }}
          >
            {record.avatar || record.name?.substring(0, 2).toUpperCase() || 'D'}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600 }}>{record.name}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>@{record.user_name}</Typography.Text>
            {(isMobile || isTablet) && (
              <div style={{ marginTop: 4 }}>
                <Tag>{getBranchName(record.branchId)}</Tag>
                {record.managerName && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>{record.managerName}</Typography.Text>
                )}
              </div>
            )}
          </div>
        </Flex>
      )
    },
    {
      title: t('dealers.colBranch'),
      key: 'branch',
      render: (_, record) => (
        <Tag icon={<span style={{ fontSize: 14 }}>{getBranchFlag(record.branchId)}</span>} color="blue">
          {getBranchName(record.branchId)}
        </Tag>
      ),
      responsive: ['md']
    },
    {
      title: t('dealers.colManager'),
      dataIndex: 'managerName',
      key: 'managerName',
      responsive: ['lg']
    },
    {
      title: t('dealers.colContact'),
      key: 'contact',
      render: (_, record) => (
        <div>
          <div><PhoneOutlined style={{ marginRight: 4 }} />{record.phone}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}><MailOutlined style={{ marginRight: 4 }} />{record.email}</Typography.Text>
        </div>
      ),
      responsive: ['md']
    },
    {
      title: t('dealers.colSubdealers'),
      key: 'subdealers',
      render: (_, record) => (
        <Tag icon={<TeamOutlined />} color="cyan">{record.stats?.totalSubdealers || 0}</Tag>
      ),
      responsive: ['lg']
    },
    {
      title: t('dealers.colActiveBids'),
      key: 'activeBids',
      render: (_, record) => record.stats?.activeBids || 0
    },
    {
      title: t('dealers.colTotalSpent'),
      key: 'totalSpent',
      render: (_, record) => (
        <span style={{ fontWeight: 600 }}>{record.stats?.totalSpent || '$0'}</span>
      )
    },
    {
      title: t('dealers.colCreatedBy'),
      key: 'createdBy',
      render: (_, record) => (
        record.createdBy ? (
          <CreatorInfo createdBy={record.createdBy} createdAt={record.createdAt} variant="compact" />
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        )
      ),
      responsive: ['lg']
    },
    {
      title: t('dealers.colActions'),
      key: 'actions',
      render: (_, record) => (
        canManageUser(record) ? (
          <Space onClick={(e) => e.stopPropagation()}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={(e) => { e.stopPropagation(); handleEdit(record); }}
              title={t('dealers.editDealer')}
            />
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(record.user_id || record.dealerId || record.autoIncrementId || record.id, record.name);
              }}
              title={t('dealers.deleteDealer')}
            />
          </Space>
        ) : null
      )
    }
  ];

  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
        <Typography.Text style={{ marginLeft: 16 }}>{t('dealers.loadingList')}</Typography.Text>
      </Flex>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 24 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t('dealers.title')}
          {isSearching && (
            <Tag color="blue" closable onClose={clearSearch} style={{ marginLeft: 8 }}>
              {getActiveFilterCount()} {t('dealers.filtersActiveSuffix')}
            </Tag>
          )}
        </Typography.Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh} title={t('common.refresh')} />
          {canCreateDealer() && (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              {t('dealers.addDealer')}
            </Button>
          )}
        </Space>
      </Flex>

      <Flex gap={8} style={{ marginBottom: 16 }}>
        <Input
          ref={quickSearchInputRef}
          placeholder={t('dealers.quickSearchPlaceholder')}
          prefix={<SearchOutlined />}
          value={searchForm.quickSearch}
          onChange={(e) => setSearchForm({ ...searchForm, quickSearch: e.target.value })}
          onKeyPress={handleQuickSearchKeyPress}
          suffix={
            searchForm.quickSearch ? (
              <Button type="text" size="small" icon={<ClearOutlined />} onClick={() => setSearchForm({ ...searchForm, quickSearch: '' })} />
            ) : (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>{t('dealers.ctrlFHint')}</Typography.Text>
            )
          }
          style={{ flex: 1 }}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={handleQuickSearch}>
          {t('common.search')}
        </Button>
      </Flex>

      <Collapse
        activeKey={showAdvancedFilters ? ['1'] : []}
        onChange={(keys) => setShowAdvancedFilters(keys.length > 0)}
        style={{ marginBottom: 16 }}
      >
        <Collapse.Panel header={t('dealers.advancedFilters')} key="1">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Input placeholder={t('common.name')} value={searchForm.name} onChange={(e) => handleFormChange('name', e.target.value)} />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Input placeholder={t('common.username')} value={searchForm.user_name} onChange={(e) => handleFormChange('user_name', e.target.value)} />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Input placeholder={t('common.email')} value={searchForm.email} onChange={(e) => handleFormChange('email', e.target.value)} />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Input placeholder={t('common.phone')} value={searchForm.phone} onChange={(e) => handleFormChange('phone', e.target.value)} />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Select
                placeholder={t('dealers.branchFilterPh')}
                allowClear
                style={{ width: '100%' }}
                value={searchForm.branchId || undefined}
                onChange={(v) => handleFormChange('branchId', v)}
                options={[
                  { value: '', label: t('dealers.allBranches') },
                  ...branches.map(b => ({ value: b.id, label: `${b.flag} ${b.name}` }))
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Select
                placeholder={t('dealers.managerFilterPh')}
                allowClear
                style={{ width: '100%' }}
                value={searchForm.managerId || undefined}
                onChange={(v) => handleFormChange('managerId', v)}
                options={[
                  { value: '', label: t('dealers.allManagers') },
                  ...managers.map(m => ({ value: m.user_id, label: m.name }))
                ]}
              />
            </Col>
          </Row>
          <Space style={{ marginTop: 16 }}>
            <Button type="primary" icon={<SearchOutlined />} onClick={handleAdvancedSearch}>{t('common.search')}</Button>
            <Button icon={<ClearOutlined />} onClick={clearSearch}>{t('dealers.clearFilters')}</Button>
          </Space>
        </Collapse.Panel>
      </Collapse>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {stats.map((stat, index) => (
          <Col xs={24} sm={12} md={8} lg={6} key={index}>
            <Card size="small">
              <Flex align="center" gap={8}>
                <span style={{ color: stat.color, fontSize: 20 }}>{stat.icon}</span>
                <div>
                  <Typography.Title level={4} style={{ margin: 0 }}>{stat.value}</Typography.Title>
                  <Typography.Text type="secondary">{stat.title}</Typography.Text>
                </div>
              </Flex>
            </Card>
          </Col>
        ))}
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={dealers}
          rowKey={r => r.user_id || r.dealerId || r.autoIncrementId || r.id}
          pagination={false}
          onRow={(record) => ({
            onClick: () => handleDealerClick(record),
            style: { cursor: 'pointer' }
          })}
          locale={{ emptyText: t('dealers.noDealersFound') }}
          scroll={{ x: 800 }}
        />
      </Card>

      {Math.ceil((totalCount || dealers.length) / limit) > 1 && (
        <Flex justify="center" align="center" gap={16} style={{ marginTop: 24 }} vertical>
          <Pagination
            total={totalCount || dealers.length}
            current={page}
            pageSize={limit}
            onChange={(value) => {
              setPage(value);
              updateURLParams(searchFilters, value);
              loadDealers(value, false);
            }}
            showSizeChanger={false}
            showTotal={(total) =>
              `${t('dealers.pageTotalShowing')} ${dealers.length} ${t('dealers.pageTotalOf')} ${total} ${t('dealers.pageTotalLabel')}`
            }
          />
        </Flex>
      )}

      <Modal
        title={editingDealer ? t('dealers.editDealer') : t('dealers.createDealer')}
        open={isFormOpen}
        onCancel={() => setIsFormOpen(false)}
        footer={null}
        width={isMobile ? '100%' : 920}
        style={isMobile ? { top: 0, paddingBottom: 0, maxWidth: '100%' } : { top: 20 }}
        bodyStyle={isMobile ? { maxHeight: 'calc(100vh - 110px)', overflowY: 'auto' } : {}}
      >
        <Form layout="vertical" labelWrap colon={false} style={{ maxWidth: '100%' }} onFinish={handleFormSubmit}>
          <div style={{ padding: '16px 24px', maxHeight: '70vh', overflowY: 'auto' }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item label={t('managers.firstName')} required>
                  <Input value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} placeholder={t('dealers.phFirst')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t('managers.lastName')} required>
                  <Input value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} placeholder={t('dealers.phLast')} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item label={t('common.username')} required>
                  <Input value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} placeholder={t('dealers.phUsername')} disabled={!!editingDealer} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t('common.email')} required>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder={t('dealers.phEmail')} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label={editingDealer ? t('dealers.passwordLeaveBlank') : t('dealers.passwordLabel')} required={!editingDealer}>
                  <Input.Password value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="••••••••" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label={t('common.confirmPassword')} required={!editingDealer || (editingDealer && formData.password.length > 0)}>
                  <Input.Password value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} placeholder="••••••••" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t('dealers.phoneLabel')} required>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder={t('dealers.phPhone')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t('managers.assignToBranch')} required>
                  <Select placeholder={t('dealers.selectBranch')} style={{ width: '100%' }} value={formData.branchId || undefined} onChange={(v) => setFormData({ ...formData, branchId: v || '', managerId: '' })} disabled={user.role === 'manager'} options={branches.map(b => ({ value: b.id, label: `${b.flag} ${b.name} - ${b.location}` }))} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t('dealers.assignToManager')} required>
                  <Select placeholder={t('dealers.selectManager')} style={{ width: '100%' }} value={formData.managerId || undefined} onChange={(v) => setFormData({ ...formData, managerId: v || '' })} disabled={user.role === 'manager' && !editingDealer} options={getAvailableManagers().filter(m => !formData.branchId || m.branchId === parseInt(formData.branchId)).map(m => ({ value: m.user_id || m.id, label: `${m.name}${m.branchName ? ` - ${m.branchName}` : ''}` }))} />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>{t('dealers.auctionAccountsTitle')} {!editingDealer && <Typography.Text type="danger">*</Typography.Text>}</Typography.Text>
                {!editingDealer && !hasAuctionAccount() && (
                  <Alert type="error" message={t('dealers.auctionAccountsRequired')} style={{ marginBottom: 16 }} />
                )}
              </Col>
              <Col span={12}>
                <AuctionAccountCard title="Copart" shortLabel="C" color="#1976d2" on={formData.copartOn} setOn={(checked) => setFormData({ ...formData, copartOn: checked })} primaryValue={formData.copartId} primaryOptions={copartAccounts.filter(acc => acc.type === 'primary' || acc.type === 'master' || !acc.type)} onPrimaryChange={(v) => setFormData({ ...formData, copartId: v || '' })} secondaryValue={formData.copartIdSecondary} secondaryOptions={copartAccounts.filter(acc => acc.type === 'secondary')} onSecondaryChange={(v) => setFormData({ ...formData, copartIdSecondary: v || '' })} />
              </Col>
              <Col span={12}>
                <AuctionAccountCard title="IAAI" shortLabel="I" color="#d32f2f" on={formData.iaaiOn} setOn={(checked) => setFormData({ ...formData, iaaiOn: checked })} primaryValue={formData.iaaiId} primaryOptions={iaaiAccounts.filter(acc => acc.type === 'primary' || acc.type === 'master' || !acc.type)} onPrimaryChange={(v) => setFormData({ ...formData, iaaiId: v || '' })} secondaryValue={formData.iaaiIdSecondary} secondaryOptions={iaaiAccounts.filter(acc => acc.type === 'secondary')} onSecondaryChange={(v) => setFormData({ ...formData, iaaiIdSecondary: v || '' })} />
              </Col>
              <Col span={12}>
                <AuctionAccountCard title={t('dealers.iaaiCanadaTitle')} shortLabel="IC" color="#d32f2f" on={formData.iaaiCanadaOn} setOn={(checked) => setFormData({ ...formData, iaaiCanadaOn: checked })} primaryValue={formData.iaaiCanadaId} primaryOptions={iaaiCanadaAccounts.filter(acc => acc.type === 'primary' || acc.type === 'master' || !acc.type)} onPrimaryChange={(v) => setFormData({ ...formData, iaaiCanadaId: v || '' })} secondaryValue={formData.iaaiCanadaIdSecondary} secondaryOptions={iaaiCanadaAccounts.filter(acc => acc.type === 'secondary')} onSecondaryChange={(v) => setFormData({ ...formData, iaaiCanadaIdSecondary: v || '' })} />
              </Col>
              <Col span={12}>
                <AuctionAccountCard title="Manheim" shortLabel="M" color="#ed6c02" on={formData.manheimOn} setOn={(checked) => setFormData({ ...formData, manheimOn: checked })} primaryValue={formData.manheimId} primaryOptions={manheimAccounts.filter(acc => acc.type === 'primary' || acc.type === 'master' || !acc.type)} onPrimaryChange={(v) => setFormData({ ...formData, manheimId: v || '' })} secondaryValue={formData.manheimIdSecondary} secondaryOptions={manheimAccounts.filter(acc => acc.type === 'secondary')} onSecondaryChange={(v) => setFormData({ ...formData, manheimIdSecondary: v || '' })} />
              </Col>
              <Col span={12}>
                <AuctionAccountCard title="ACV" shortLabel="A" color="#9c27b0" on={formData.acvOn} setOn={(checked) => setFormData({ ...formData, acvOn: checked })} primaryValue={formData.acvId} primaryOptions={acvAccounts.filter(acc => acc.type === 'primary' || acc.type === 'master' || !acc.type)} onPrimaryChange={(v) => setFormData({ ...formData, acvId: v || '' })} secondaryValue={formData.acvIdSecondary} secondaryOptions={acvAccounts.filter(acc => acc.type === 'secondary')} onSecondaryChange={(v) => setFormData({ ...formData, acvIdSecondary: v || '' })} />
              </Col>

              <Col span={24}>
                <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>{t('dealers.auctionLimitsSection')}</Typography.Text>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item label={<><span>{t('dealers.bidLimitLabel')}</span> <Tooltip title={t('dealers.tooltips.bidLimit')}><Button type="text" size="small" icon={<InfoCircleOutlined />} /></Tooltip></>}>
                  <Input type="number" value={formData.bidLimit} onChange={(e) => setFormData({ ...formData, bidLimit: e.target.value })} placeholder="0" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label={<><span>{t('dealers.activeBidsAmountLabel')}</span> <Tooltip title={t('dealers.tooltips.activeBidsLimit')}><Button type="text" size="small" icon={<InfoCircleOutlined />} /></Tooltip></>}>
                  <Input type="number" value={formData.activeBidsLimit} onChange={(e) => setFormData({ ...formData, activeBidsLimit: e.target.value })} placeholder="0" />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  label={
                    <Flex align="center" gap={6} wrap="nowrap">
                      <span style={{ whiteSpace: 'nowrap' }}>{t('dealers.activeBidsSumLimitLabel')}</span>
                      <Tooltip title={t('dealers.tooltips.activeBidsSumLimit')}>
                        <Button type="text" size="small" icon={<InfoCircleOutlined />} style={{ flexShrink: 0 }} />
                      </Tooltip>
                    </Flex>
                  }
                >
                  <Input type="number" value={formData.activeBidsSumLimit} onChange={(e) => setFormData({ ...formData, activeBidsSumLimit: e.target.value })} placeholder="0" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={<><span>{t('dealers.purchasedCarsLimitLabel')}</span> <Tooltip title={t('dealers.tooltips.purchasedCarsLimit')}><Button type="text" size="small" icon={<InfoCircleOutlined />} /></Tooltip></>}>
                  <Input type="number" value={formData.purchasedCarsLimit} onChange={(e) => setFormData({ ...formData, purchasedCarsLimit: e.target.value })} placeholder="0" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={<><span>{t('dealers.lotsEventSettingLabel')}</span> <Tooltip title={t('dealers.tooltips.lotsEventSetting')}><Button type="text" size="small" icon={<InfoCircleOutlined />} /></Tooltip></>}>
                  <Select style={{ width: '100%' }} value={formData.lotsEventSetting || 'hide'} onChange={(v) => setFormData({ ...formData, lotsEventSetting: v || 'hide' })} options={[{ value: 'hide', label: t('dealers.visibilityHide') }, { value: 'show', label: t('dealers.visibilityShow') }]} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={<><span>{t('dealers.purchasedLotsSettingLabel')}</span> <Tooltip title={t('dealers.tooltips.purchasedLotsSetting')}><Button type="text" size="small" icon={<InfoCircleOutlined />} /></Tooltip></>}>
                  <Select style={{ width: '100%' }} value={formData.purchasedLotsSetting || 'winning_lots'} onChange={(v) => setFormData({ ...formData, purchasedLotsSetting: v || 'winning_lots' })} options={[{ value: 'hide', label: t('dealers.visibilityHide') }, { value: 'partlyShow', label: t('dealers.visibilityPartlyShow') }, { value: 'winning_lots', label: t('dealers.visibilityWinningLots') }, { value: 'show', label: t('dealers.visibilityShow') }]} />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Divider style={{ margin: '16px 0' }} />
                <Typography.Text strong style={{ display: 'block', marginBottom: 16 }}>{t('dealers.calculatorSettingsSection')}</Typography.Text>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label={t('dealers.showCalculatorWidget')}>
                  <Switch checked={formData.show_calculator} onChange={(checked) => setFormData({ ...formData, show_calculator: checked })} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label={t('dealers.showHelperTool')}>
                  <Switch checked={formData.show_helper} onChange={(checked) => setFormData({ ...formData, show_helper: checked })} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label={t('dealers.showDiscountRow')}>
                  <Switch checked={formData.show_discount} onChange={(checked) => setFormData({ ...formData, show_discount: checked })} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label={t('dealers.fixedDiscountAmount')} help={t('dealers.fixedDiscountHelp')}>
                  <Input type="number" min={0} step={0.01} style={{ width: '100%' }} value={formData.discountAmount} onChange={(e) => setFormData({ ...formData, discountAmount: parseFloat(e.target.value) || 0 })} placeholder="0" />
                </Form.Item>
              </Col>
            </Row>
          </div>
          <div style={{ padding: '16px 24px', borderTop: '1px solid #f0f0f0' }}>
            <Space>
              <Button onClick={() => setIsFormOpen(false)}>{t('common.cancel')}</Button>
              <Button type="primary" htmlType="submit" disabled={!editingDealer && !hasAuctionAccount()}>
                {editingDealer ? t('common.update') : t('common.create')}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default DealersPage;